import { Autocomplete, Divider, Drawer, Grid, IconButton, InputAdornment, Table, TableBody, TableCell, TableContainer, TableRow, Typography } from "@mui/material"
import { FC, useEffect, useState } from "react"
import CloseIcon from "@mui/icons-material/Close"
import CustomTable from "../../../../components/common/CustomTable/CustomTable"
import { AccountReceivable } from "../../../../types/slices/accountReceivableType"
import { formatDate } from "../../../../toolbox/helpers/formatDateHelper"
import { moneyCL } from "../../../../toolbox/helpers/money-helpers"
import { Card, CardContent, Button, TextField, MenuItem, Select, InputLabel, FormControl } from "@mui/material"
import { useBankStore } from "../../../../hooks/useBankStore"
import { ApiStatus } from "../../../../types/api/status"
import { CustomSnackbar } from "../../../../components/common/CustomSnackbar/CustomSnackbar"
import { useAccountReceivableStore } from "../../../../hooks/useAccountReceivableStore"
import { ModalConfirm } from "../../../../components/common/ModalConfirm/ModalConfirm"

interface Props {
    open: boolean
    onClose: () => void
    selecteds: AccountReceivable[]
    onList1: () => Promise<void>
    onList2: () => Promise<void>
}

const StyledTableCell = ({ children, ...props }) => (
    <TableCell sx={{ ...props.sx }}>
        {children}
    </TableCell>
)

interface PaymentVoucher {
    idway_to_pay: number
    idsource_bank: number | undefined
    idreceiving_bank: number | undefined
    series: string
    entry_date: string
    document_date: string
    amount: number | undefined
}

const wayToPays = [
    { value: "24", label: "Transferencia bancaria" },
    { value: "18", label: "Cheque" },
]

const today = new Date();
const formattedDate = today.toISOString().split('T')[0];

export const PayReceivableDrawer: FC<Props> = (props) => {
    const { open, onClose, selecteds, onList1, onList2 } = props

    const { status: statusBank, banks, getBanks } = useBankStore()
    const { status: statusPay, payAccountsReceivables } = useAccountReceivableStore()

    const [selectedsInvoices, setSelectedInvoices] = useState<any[]>([])
    const [total, setTotal] = useState<number>(0)
    const [selectedWayToPay, setSelectedWayToPay] = useState<any>(null)
    const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false)

    const [formData, setFormData] = useState<PaymentVoucher[]>([{
        idway_to_pay: 24,
        idsource_bank: undefined,
        idreceiving_bank: undefined,
        series: "",
        entry_date: formattedDate,
        document_date: formattedDate,
        amount: undefined,
    }])

    useEffect(() => {
        getBanks()
    }, [])

    useEffect(() => {
        if (selecteds.length > 0) {
            const data = selecteds?.map(row => ({ ...row, amount_pay: row.debt }))
            setSelectedInvoices(data || [])
        }
    }, [selecteds])

    useEffect(() => {
        if (selectedsInvoices.length > 0) {
            const total = selectedsInvoices.reduce((acc, row) => acc + Number(row.amount_pay), 0)
            setTotal(total)
        } else {
            setTotal(0)
        }
    }, [selectedsInvoices])

    const handleChange = (index, field) => (event) => {
        const { value } = event.target

        if (field === 'amount' && value && !/^\d+$/.test(value)) return

        setFormData(prevData =>
            prevData.map((row, rowIndex) =>
                rowIndex === index ? { ...row, [field]: value } : row
            )
        )
    }

    const onAddWayToPay = () => {
        if (!selectedWayToPay) return CustomSnackbar('error', 'Seleccione una método de pago.')

        setFormData([...formData, {
            idway_to_pay: parseInt(selectedWayToPay?.value),
            idsource_bank: undefined,
            idreceiving_bank: undefined,
            series: "",
            entry_date: formattedDate,
            document_date: formattedDate,
            amount: undefined,
        }])
    }

    const onDelete = (index) => {
        const data = formData.filter((row, rowIndex) => rowIndex !== index)
        setFormData(data || [])
    }

    const setAmountAccount = (rowSelected, value) => {
        setSelectedInvoices(prev =>
            prev.map(row =>
                row.id === rowSelected.id
                    ? { ...row, amount_pay: value === "" ? "" : Math.min(Number(value), rowSelected.debt) }
                    : row
            )
        )
    }

    const onPay = () => {
        const isValid = formData.every(voucher =>
            voucher.idway_to_pay &&
            voucher.series.trim() !== "" &&
            voucher.entry_date.trim() !== "" &&
            voucher.document_date.trim() !== "" &&
            voucher.amount !== undefined && voucher.amount > 0 &&
            (voucher.idway_to_pay === 24
                ? voucher.idsource_bank !== undefined && voucher.idreceiving_bank !== undefined
                : true)
        )

        if (!isValid) return CustomSnackbar('error', 'Complete los comprobantes de pago.')
        setShowConfirmDialog(true)
    }

    const onConfirmPay = async () => {
        const resp = await payAccountsReceivables({
            account_receivable: selectedsInvoices?.map(row => ({
                id: row.id,
                amount_pay: row.amount_pay
            })) || [],
            payment_voucher: formData
        })
        if (resp === true) {
            onList2()
            onClose()
            onList1()
        }
    }

    const columns = [
        { type: 'text', field: 'folio', label: 'N° Folio', align: 'center' },
        { type: 'text', field: 'way_to_pay', label: 'Forma de pago', align: 'center' },
        { type: 'text', field: 'deadline', label: 'Fecha límite', align: 'center', format: (row) => formatDate(row.deadline) },
        { type: 'text', field: 'amount_paid', label: 'Monto pagado', align: 'center', format: (row) => moneyCL(row.amount_paid) },
        { type: 'text', field: 'debt', label: 'Saldo pendiente', align: 'center', format: (row) => moneyCL(row.debt) },
        { type: 'text', field: 'total', label: 'Total factura', align: 'center', format: (row) => moneyCL(row.total) },
        { type: 'text', field: 'amount_pay', label: 'Monto a pagar', align: 'center', isEditable: setAmountAccount, startAdornment: '$' },
    ]

    return (
        <Drawer open={open} onClose={onClose} anchor="right">
            <Grid container sx={{ width: 1050, padding: 3 }} spacing={2}>
                {/* Botón de cierre */}
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        color: "gray",
                    }}
                >
                    <CloseIcon />
                </IconButton>

                {/* Título */}
                <Grid item xs={12}>
                    <Typography variant="h6" fontWeight="bold">
                        Cuentas por cobrar
                    </Typography>
                </Grid>

                <Grid item xs={12}>
                    <Divider />
                </Grid>

                {/* Tabla de cuentas */}
                <Grid item xs={12}>
                    <CustomTable
                        columns={columns}
                        rows={selectedsInvoices || []}
                        onRowClick={() => { }}
                        dense
                        withoutPagination
                        withoutTitle
                    />
                </Grid>

                {/* Totales */}
                <Grid item xs={12} display="flex" justifyContent="flex-end">
                    <TableContainer sx={{ maxWidth: 300 }}>
                        <Table size="small">
                            <TableBody>
                                <TableRow>
                                    <StyledTableCell
                                        align="center"
                                        sx={{ background: "#69ABC3", color: "#fff", fontWeight: "bold" }}
                                    >
                                        Total a pagar
                                    </StyledTableCell>
                                    <StyledTableCell align="center">{moneyCL(total)}</StyledTableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>

                <Grid item xs={12}>
                    <Divider />
                </Grid>

                {/* Selector de forma de pago */}
                <Grid item container xs={12} alignItems="center" spacing={2}>
                    <Grid item xs={8}>
                        <Autocomplete
                            id="idway_to_pay"
                            size="small"
                            value={selectedWayToPay || null}
                            options={wayToPays || []}
                            getOptionLabel={(option) => option?.label || ""}
                            isOptionEqualToValue={(option, value) => option.label === value?.label}
                            renderInput={(params) => <TextField {...params} label="Método de pago" size="small" />}
                            onChange={(e, newValue: any) => setSelectedWayToPay(newValue)}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={2}>
                        <Button onClick={onAddWayToPay} variant="contained" size="small" fullWidth>
                            Agregar
                        </Button>
                    </Grid>
                    <Grid item xs={2}>
                        <Button onClick={onPay} variant="contained" color="success" size="small" fullWidth>
                            Pagar
                        </Button>
                    </Grid>
                </Grid>

                {/* Tarjetas de transferencias */}
                <Grid item container xs={12} spacing={2}>
                    {formData.map((row, index) => (
                        <Grid item xs={12} key={index}>
                            <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
                                <CardContent>
                                    <Grid container spacing={2} alignItems="center">
                                        {/* Título de la tarjeta */}
                                        <Grid item xs={6}>
                                            <Typography variant="subtitle1" fontWeight="bold">
                                                Comprobante N° {index + 1} - {row.idway_to_pay === 24 ? "Transferencia" : "Cheque"}
                                            </Typography>
                                        </Grid>
                                        {/* Botón de eliminar */}
                                        <Grid item xs={6} textAlign="right">
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                size="small"
                                                onClick={() => onDelete(index)}
                                            >
                                                Eliminar
                                            </Button>
                                        </Grid>

                                        {/* Campos de formulario */}
                                        <Grid item xs={row.idway_to_pay === 24 ? 6 : 12}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Origen (Banco)</InputLabel>
                                                <Select
                                                    value={row.idsource_bank}
                                                    onChange={handleChange(index, "idsource_bank")}
                                                    disabled={statusBank === ApiStatus.FETCHING}
                                                >
                                                    <MenuItem value="">-- Seleccione --</MenuItem>
                                                    {banks.map((banco) => (
                                                        <MenuItem key={banco.id} value={banco.id}>
                                                            {banco.name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        {row.idway_to_pay === 24 && (
                                            <Grid item xs={6}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Destino (Banco)</InputLabel>
                                                    <Select
                                                        value={row.idreceiving_bank}
                                                        onChange={handleChange(index, "idreceiving_bank")}
                                                        disabled={statusBank === ApiStatus.FETCHING}
                                                    >
                                                        <MenuItem value="">-- Seleccione --</MenuItem>
                                                        {banks.map((banco) => (
                                                            <MenuItem key={banco.id} value={banco.id}>
                                                                {banco.name}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        )}

                                        <Grid item xs={3}>
                                            <TextField
                                                label="Serie de Transferencia"
                                                fullWidth
                                                size="small"
                                                value={row.series}
                                                onChange={handleChange(index, "series")}
                                            />
                                        </Grid>

                                        <Grid item xs={3}>
                                            <TextField
                                                label="Fecha de ingreso"
                                                type="date"
                                                fullWidth
                                                size="small"
                                                value={row.entry_date}
                                                onChange={handleChange(index, "entry_date")}
                                                inputProps={{
                                                    max: formattedDate
                                                }}
                                            />
                                        </Grid>

                                        <Grid item xs={3}>
                                            <TextField
                                                label="Fecha del documento"
                                                type="date"
                                                fullWidth
                                                size="small"
                                                value={row.document_date}
                                                onChange={handleChange(index, "document_date")}
                                                inputProps={{
                                                    max: formattedDate
                                                }}
                                            />
                                        </Grid>

                                        <Grid item xs={3}>
                                            <TextField
                                                label="Monto"
                                                type="text"
                                                fullWidth
                                                size="small"
                                                value={row.amount}
                                                onChange={
                                                    handleChange(index, "amount")
                                                }
                                                inputProps={{
                                                    min: 1,
                                                    inputMode: "numeric",
                                                    pattern: "[0-9]*"
                                                }}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <span>$</span>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Grid>
            {showConfirmDialog && (
                <ModalConfirm
                    open={showConfirmDialog}
                    closeModal={() => { setShowConfirmDialog(false) }}
                    onConfirm={onConfirmPay}
                    status={statusPay === ApiStatus.FETCHING}
                    title="Confirmar pago"
                    text="¿Está seguro de realizar el pago?"
                />
            )}
        </Drawer>
    )
}